/**
 * THE PAGE AS THE COGENT SEES IT: the viewer's screen, read from its semantic HTML into a compact outline the cogent
 * reasons over and acts on by ref (`actions.ts`). One element per line, indented by nesting:
 *
 *   @setup region "Your setup"
 *     @setup-line status "Mira is writing its first program"
 *     @setup-action button "Ask Mira to write its first program"
 *   e3 heading "Leaderboard" [level 2]
 *     e4 listitem "Rank 1 sam khors Score 412.0"
 *
 * A ref is `@<data-testid or id>` when the element has one no other element shares (stable across turns), else
 * `e<n>`, numbered in document order for this outline. It covers landmarks, headings, labelled sections, live status
 * lines, buttons, links, inputs and disclosures, and skips the cogent widget, hidden, aria-hidden and inert elements.
 * Built when the viewer sends a line, from the DOM as it is then.
 */

/** The most characters an outline holds; `client_data` is dropped whole above 8 KB. */
export const OUTLINE_MAX = 6000
const NAME_MAX = 100
const VALUE_MAX = 40

export type Outline = {
  text: string
  /** Each ref in the text, to the element it names. */
  refs: Map<string, HTMLElement>
  /** How many elements the outline names (before any cut). */
  elements: number
}

const LANDMARKS: Record<string, string> = {
  HEADER: 'banner',
  NAV: 'navigation',
  MAIN: 'main',
  ASIDE: 'complementary',
  FOOTER: 'contentinfo',
  DIALOG: 'dialog',
  OUTPUT: 'status',
  LI: 'listitem',
}
const KEPT = new Set([
  'banner',
  'navigation',
  'main',
  'complementary',
  'contentinfo',
  'region',
  'form',
  'search',
  'dialog',
  'alertdialog',
  'heading',
  'status',
  'alert',
  'log',
  'progressbar',
  'button',
  'link',
  'textbox',
  'searchbox',
  'checkbox',
  'radio',
  'switch',
  'combobox',
  'listbox',
  'slider',
  'spinbutton',
  'tab',
  'tabpanel',
  'menuitem',
  'option',
  'listitem',
])
/** Roles named by what they say; the others only by a label. */
const FROM_CONTENT = new Set([
  'heading',
  'status',
  'alert',
  'log',
  'button',
  'link',
  'tab',
  'menuitem',
  'option',
  'listitem',
])
const INPUT_ROLES: Record<string, string> = {
  button: 'button',
  submit: 'button',
  reset: 'button',
  image: 'button',
  checkbox: 'checkbox',
  radio: 'radio',
  range: 'slider',
  number: 'spinbutton',
  search: 'searchbox',
}
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'SVG'])
/** Ids a person wrote (`setup`, `op-board`); React's generated ones (`«r3»`, `_R_1_`) change between renders. */
const AUTHORED_ID = /^[a-z][a-z0-9-]*$/

const SENSITIVE = '[data-sensitive]:not([data-sensitive="false"])'

/** Not on screen, or not for the cogent: its whole subtree is skipped. */
function skipped(el: Element): boolean {
  if (SKIPPED_TAGS.has(el.tagName.toUpperCase())) return true
  // The widget is portaled onto the body: skipping its root skips it.
  if (el.matches('[data-testid=cogent-panel], [data-site-cogent]')) return true
  if ((el as HTMLElement).hidden || el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return true
  // A closed <details> shows only its summary.
  const parent = el.parentElement
  if (parent?.tagName === 'DETAILS' && !(parent as HTMLDetailsElement).open && el.tagName !== 'SUMMARY') return true
  // A region the page marks sensitive is not read at all; a marked field keeps its row but never its value or options.
  if (el.matches(SENSITIVE) && !['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return true
  if (parent?.closest(`select${SENSITIVE}`)) return true
  const style = getComputedStyle(el)
  return style.display === 'none' || style.visibility === 'hidden'
}

function roleOf(el: Element): string | null {
  const explicit = el.getAttribute('role')?.trim().split(/\s+/)[0]
  if (explicit) return KEPT.has(explicit) ? explicit : null
  const tag = el.tagName
  if (/^H[1-6]$/.test(tag)) return 'heading'
  if (tag === 'A') return el.hasAttribute('href') ? 'link' : null
  if (tag === 'BUTTON' || tag === 'SUMMARY') return 'button'
  if (tag === 'TEXTAREA') return 'textbox'
  if (tag === 'SELECT') return (el as HTMLSelectElement).multiple ? 'listbox' : 'combobox'
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type
    return type === 'hidden' ? null : (INPUT_ROLES[type] ?? 'textbox')
  }
  // A section or form is a landmark only when it is labelled.
  if (tag === 'SECTION' || tag === 'FORM')
    return el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby')
      ? tag === 'FORM'
        ? 'form'
        : 'region'
      : null
  if (LANDMARKS[tag]) return LANDMARKS[tag]
  const live = el.getAttribute('aria-live')
  return live && live !== 'off' ? 'status' : null
}

const squash = (text: string, max: number) => {
  const t = text.replace(/\s+/g, ' ').trim()
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t
}

/** What a screen reader reads of `node`: its text, without what is hidden from it. */
function textOf(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
  if (node.nodeType !== Node.ELEMENT_NODE) return ''
  const el = node as Element
  if (
    SKIPPED_TAGS.has(el.tagName.toUpperCase()) ||
    (el as HTMLElement).hidden ||
    el.getAttribute('aria-hidden') === 'true' ||
    // Names reach beyond the walk: a label or `aria-labelledby` target can sit inside a marked region.
    el.closest(SENSITIVE)
  )
    return ''
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return ''
  const label = el.getAttribute('aria-label')
  if (label) return label
  return Array.from(el.childNodes, textOf).join(' ')
}

function nameOf(el: Element, role: string): string {
  const by = el.getAttribute('aria-labelledby')
  if (by)
    return squash(
      by
        .split(/\s+/)
        .map((id) => {
          const target = document.getElementById(id)
          return target ? textOf(target) : ''
        })
        .join(' '),
      NAME_MAX
    )
  const label = el.getAttribute('aria-label')
  if (label) return squash(label, NAME_MAX)
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    const labels = Array.from(el.labels ?? [], textOf).join(' ')
    if (labels.trim()) return squash(labels, NAME_MAX)
    if (el instanceof HTMLInputElement && el.type === 'submit') return el.value || 'Submit'
    return squash(el.getAttribute('placeholder') ?? el.getAttribute('title') ?? '', NAME_MAX)
  }
  if (FROM_CONTENT.has(role)) {
    const text = squash(textOf(el), NAME_MAX)
    if (text) return text
  }
  return squash(el.getAttribute('title') ?? '', NAME_MAX)
}

function statesOf(el: Element, role: string): string[] {
  const states: string[] = []
  const expanded = el.getAttribute('aria-expanded')
  const details =
    el.tagName === 'SUMMARY' && el.parentElement?.tagName === 'DETAILS'
      ? (el.parentElement as HTMLDetailsElement)
      : null
  if (expanded === 'true' || details?.open) states.push('expanded')
  else if (expanded === 'false' || details) states.push('collapsed')
  const level = el.getAttribute('aria-level') ?? /^H([1-6])$/.exec(el.tagName)?.[1]
  if (role === 'heading' && level) states.push(`level ${level}`)
  if ((el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') states.push('disabled')
  if (
    (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio') && el.checked) ||
    el.getAttribute('aria-checked') === 'true'
  )
    states.push('checked')
  if (el.getAttribute('aria-pressed') === 'true') states.push('pressed')
  if (el.getAttribute('aria-selected') === 'true') states.push('selected')
  const current = el.getAttribute('aria-current')
  if (current && current !== 'false') states.push('current')
  if (el.getAttribute('aria-invalid') === 'true') states.push('invalid')
  if (
    (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
    role !== 'checkbox' &&
    role !== 'radio' &&
    role !== 'button' &&
    el.value &&
    // A secret's value is never sent: a password, a card or one-time code, or a field the page marks sensitive.
    !(el instanceof HTMLInputElement && el.type === 'password') &&
    !/(^|\s)(cc-[\w-]+|one-time-code|[\w-]*-password)(\s|$)/i.test(el.getAttribute('autocomplete') ?? '') &&
    !el.matches(SENSITIVE)
  )
    states.push(`value "${squash(el.value, VALUE_MAX)}"`)
  if (el instanceof HTMLAnchorElement && el.href) {
    // A browser keeps an address it can't parse as written, and one bad link must not stop the outline.
    const to = URL.canParse(el.href, location.href) ? new URL(el.href, location.href) : null
    // Query strings and fragments can carry tokens.
    states.push(to?.origin === location.origin ? `to ${to.pathname}` : 'external')
  }
  // Pressing it cannot be undone: the page asks the viewer instead (`actions.ts`).
  if (el.hasAttribute('data-irreversible')) states.push('irreversible')
  return states
}

const stableRef = (el: Element): string | null =>
  el.getAttribute('data-testid') ?? (AUTHORED_ID.test(el.id) ? el.id : null)

/** The outline of what is on screen under `root` (the whole page by default). */
export function outlineOf(root: Element = document.body): Outline {
  type Row = { el: HTMLElement; role: string; depth: number }
  const rows: Row[] = []
  const walk = (el: Element, depth: number) => {
    for (const child of Array.from(el.children)) {
      if (skipped(child)) continue
      const role = roleOf(child)
      // A list item holding controls or headings is only their container: its own name would repeat them.
      const container =
        role === 'listitem' &&
        child.querySelector('a[href], button, input, select, textarea, summary, h1, h2, h3, h4, h5, h6, [role]')
      if (role && !container) {
        rows.push({ el: child as HTMLElement, role, depth })
        walk(child, depth + 1)
      } else walk(child, depth)
    }
  }
  walk(root, 0)

  const seen = new Map<string, number>()
  for (const { el } of rows) {
    const ref = stableRef(el)
    if (ref) seen.set(ref, (seen.get(ref) ?? 0) + 1)
  }
  const refs = new Map<string, HTMLElement>()
  let n = 0
  let text = ''
  let shown = 0
  for (const { el, role, depth } of rows) {
    const stable = stableRef(el)
    const ref = stable && seen.get(stable) === 1 ? `@${stable}` : `e${++n}`
    const states = statesOf(el, role)
    const line = `${'  '.repeat(depth)}${ref} ${role} ${JSON.stringify(nameOf(el, role))}${
      states.length ? ` [${states.join(', ')}]` : ''
    }\n`
    const rest = `… ${rows.length - shown} more elements not shown\n`
    if (text.length + line.length + rest.length > OUTLINE_MAX) {
      text += rest
      break
    }
    text += line
    refs.set(ref, el)
    shown++
  }
  return { text: text.trimEnd(), refs, elements: rows.length }
}
